import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type {
  Defect,
  DefectStatus,
  GetDefectsParams,
  GetDefectsResponse,
  UpdateDefectBody,
  UpdateDefectGeometryBody,
  UpdateDefectGeometryResponse,
  UpdateDefectResponse,
} from "@aeriosiq/types";
import {
  useAuditLogAppendMutation,
  type AuditLogAppendResponse,
} from "../audit/queries.js";
import {
  describeUndoEntry,
  useUndoStackStore,
  type UpdateDefectBefore,
} from "../analysis/state/undoStackStore.js";
import {
  fetchAllListPages,
  LIST_PAGE_SIZE_MAX,
} from "../analysis/list/fetchAllListPages.js";
import { OVERLAY_STALE_TIME_MS } from "../analysis/overlayQueryPolicy.js";

export type { UpdateDefectBody };

export const defectKeys = {
  all: ["defects"] as const,
  list: (inspectionId: string, params: GetDefectsParams) =>
    [...defectKeys.all, "list", inspectionId, params] as const,
};

/**
 * PERF-06 (audit I-6) — canonical params for an image-scoped ("overlay")
 * defects query. The Viewer-overlay mount (`AnalysisFeature`) and the
 * overlay prefetch (`useOverlayPrefetch`) both build their params through
 * THIS helper, so the two query keys are structurally identical — which is
 * what makes the prefetch a cache hit instead of wasted work.
 */
export function imageScopedDefectsParams(
  imageId: string | null,
  pageSize = 200,
): GetDefectsParams {
  return { pageSize, ...(imageId ? { imageId } : {}) };
}

/**
 * PERF-06 (audit I-6) — shared key + fetcher builder; the defects twin of
 * `buildFindingsListQuery` (see that builder for the key-drift rationale).
 */
export function buildDefectsListQuery(
  api: Pick<ReturnType<typeof useApi>, "get">,
  inspectionId: string,
  params: GetDefectsParams = {},
): {
  queryKey: ReturnType<typeof defectKeys.list>;
  queryFn: () => Promise<GetDefectsResponse>;
} {
  const url = `/data-acquisitions/${encodeURIComponent(inspectionId)}/defects`;
  return {
    queryKey: defectKeys.list(inspectionId, params),
    queryFn: async () => {
      const requested = params.pageSize ?? 0;
      // Within the contract cap → the exact single-call behaviour.
      if (requested <= LIST_PAGE_SIZE_MAX) {
        const response = await api.get<GetDefectsResponse>(url, {
          params: toQueryParams(params),
          paramsSerializer: { indexes: null }, // status[] -> repeated keys
        });
        return response.data;
      }
      // Above the cap (the panel's pageSize=2000 ask): the BE silently
      // clamps to 200, so page-walk and stitch — see fetchAllListPages.
      return fetchAllListPages<Defect, GetDefectsResponse>(
        async (page, pageSize) => {
          const response = await api.get<GetDefectsResponse>(url, {
            params: toQueryParams({ ...params, page, pageSize }),
            paramsSerializer: { indexes: null },
          });
          return response.data;
        },
        requested,
      );
    },
  };
}

export function useDefectsQuery(
  inspectionId: string,
  params: GetDefectsParams = {},
) {
  const api = useApi();
  return useQuery<GetDefectsResponse>({
    ...buildDefectsListQuery(api, inspectionId, params),
    enabled: inspectionId.length > 0,
    placeholderData: (prev) => prev,
    // N-5 (audit 3.4, 2026-06-11) — paint-then-correct for the per-image
    // overlay queries; see useFindingsQuery / overlayQueryPolicy.ts.
    ...(params.imageId
      ? { staleTime: OVERLAY_STALE_TIME_MS, refetchOnMount: "always" as const }
      : {}),
  });
}

function toQueryParams(p: GetDefectsParams) {
  const out: Record<string, unknown> = {};
  if (p.page !== undefined) out.page = p.page;
  if (p.pageSize !== undefined) out.pageSize = p.pageSize;
  if (p.status && p.status.length > 0) out.status = p.status;
  // 5.6.0 (R58-L-07) — additive scope.
  if (p.cursor) out.cursor = p.cursor;
  if (p.imageId) out.imageId = p.imageId;
  // 5.8.0 (R58-L-05) — defect text search. Empty/whitespace-only is
  // dropped client-side; the BE additionally trims and treats empty as
  // omitted, so this is defence-in-depth.
  if (p.q && p.q.trim().length > 0) out.q = p.q.trim();
  // 5.8.0 (R58-L-05) — Track C.4 — defectClassIds typeahead AND-axis.
  // CSV-encoded by the Axios `paramsSerializer: { indexes: null }`
  // setting on the parent query: `defectClassIds=<uuid>&defectClassIds=<uuid>`.
  if (p.defectClassIds && p.defectClassIds.length > 0) {
    out.defectClassIds = p.defectClassIds;
  }
  return out;
}

export function statusToStatusPill(s: DefectStatus | "reviewed") {
  switch (s) {
    case "detected":
      return "open" as const;
    case "suspected":
      return "needs_review" as const;
    case "reviewed":
      // Story 024 — intermediate state between `detected` and
      // `confirmed`. Maps to `needs_review` until the contract bump
      // 5.2.0 introduces a dedicated pill semantic.
      return "needs_review" as const;
    case "confirmed":
      return "in_progress" as const;
    case "repaired":
      return "closed" as const;
  }
}

interface UpdateDefectContext {
  previousLists: { key: readonly unknown[]; data: GetDefectsResponse }[];
}

export function useUpdateDefectMutation(inspectionId: string) {
  const qc = useQueryClient();
  const api = useApi();

  return useMutation<
    UpdateDefectResponse,
    Error & { errorId?: string },
    { defectId: string; body: UpdateDefectBody },
    UpdateDefectContext
  >({
    mutationFn: async ({ defectId, body }) => {
      const response = await api.patch<UpdateDefectResponse>(
        `/defects/${encodeURIComponent(defectId)}`,
        body,
      );
      return response.data;
    },
    onMutate: async ({ defectId, body }) => {
      const queryFamily = [...defectKeys.all, "list", inspectionId] as const;
      await qc.cancelQueries({ queryKey: queryFamily });

      const cached = qc.getQueriesData<GetDefectsResponse>({
        queryKey: queryFamily,
      });
      const ctx: UpdateDefectContext = {
        previousLists: cached.map(([key, data]) => ({
          key,
          data: data ?? { items: [], total: 0, page: 1, pageSize: 50 },
        })),
      };

      for (const [key, data] of cached) {
        if (!data) continue;
        qc.setQueryData<GetDefectsResponse>(key, {
          ...data,
          items: data.items.map(
            (d): Defect =>
              d.id === defectId
                ? {
                    ...d,
                    ...(body.status !== undefined
                      ? { status: body.status }
                      : {}),
                    ...(body.severity !== undefined
                      ? { severity: body.severity }
                      : {}),
                    // Story 020 — defectClassId update is best-effort
                    // optimistic; the wire payload doesn't have a canonical
                    // `defectClassId` field on `Defect` (it surfaces via
                    // `catalogueLabel` derived server-side), so we leave
                    // the label and let the server response replace it on
                    // settle. Reaching this branch is enough to keep the
                    // mutation cache consistent.
                  }
                : d,
          ),
        });
      }
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      for (const { key, data } of ctx.previousLists) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: [...defectKeys.all, "list", inspectionId],
      });
    },
  });
}

/**
 * ANA-R3-GEOM-04 — reshape an existing defect's geometry in place.
 *
 * Sends `PATCH /defects/:id/geometry` with a flat PIXEL `path`
 * (`UpdateDefectGeometryBody`). The BE recomputes `area` + re-derives
 * `bbox` and returns the updated `Defect` (normalised read `path`).
 *
 * The mutation optimistically patches the cached defect's `path` (so the
 * Viewer overlay re-renders the new outline immediately) and rolls the
 * whole list family back on error. `onSettled` invalidates so `area` /
 * `bbox` reflect the server-recomputed values.
 *
 * `optimisticPath` is the NORMALISED 0..1 read-shape path (what the
 * Viewer overlay consumes) — the caller derives it from the same editor
 * points it serialises to the pixel `body.path`, so the optimistic
 * overlay matches the committed shape with no extra round-trip.
 */
export interface UpdateDefectGeometryVars {
  defectId: string;
  body: UpdateDefectGeometryBody;
  /** Normalised 0..1 path used for the optimistic overlay update. */
  optimisticPath: number[];
}

interface UpdateGeometryContext {
  previousLists: { key: readonly unknown[]; data: GetDefectsResponse }[];
}

export function useUpdateDefectGeometryMutation(inspectionId: string) {
  const qc = useQueryClient();
  const api = useApi();

  return useMutation<
    UpdateDefectGeometryResponse,
    Error & { errorId?: string },
    UpdateDefectGeometryVars,
    UpdateGeometryContext
  >({
    mutationFn: async ({ defectId, body }) => {
      const response = await api.patch<UpdateDefectGeometryResponse>(
        `/defects/${encodeURIComponent(defectId)}/geometry`,
        body,
      );
      return response.data;
    },
    onMutate: async ({ defectId, optimisticPath }) => {
      const queryFamily = [...defectKeys.all, "list", inspectionId] as const;
      await qc.cancelQueries({ queryKey: queryFamily });

      const cached = qc.getQueriesData<GetDefectsResponse>({
        queryKey: queryFamily,
      });
      const ctx: UpdateGeometryContext = {
        previousLists: cached.map(([key, data]) => ({
          key,
          data: data ?? { items: [], total: 0, page: 1, pageSize: 50 },
        })),
      };

      for (const [key, data] of cached) {
        if (!data) continue;
        qc.setQueryData<GetDefectsResponse>(key, {
          ...data,
          items: data.items.map(
            (d): Defect =>
              d.id === defectId
                ? // Optimistically swap the read `path` so the Viewer overlay
                  // re-renders the edited outline at once; `area`/`bbox` are
                  // left to the server (refetched on settle).
                  { ...d, path: optimisticPath, geometryOrigin: "manual" }
                : d,
          ),
        });
      }
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      for (const { key, data } of ctx.previousLists) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: [...defectKeys.all, "list", inspectionId],
      });
    },
  });
}

/**
 * Story 064 — soft-delete a defect. The dev-api routes `DELETE
 * /defects/:id` to remove the row from the in-memory store; the real
 * BE soft-deletes by stamping `deleted_at` / `deleted_by`.
 *
 * Spec: shipping under the existing `updateDefect` umbrella in
 * docs/backend-requirements/009-update-defect.md §"Open questions" —
 * a follow-up to formalise a dedicated `softDeleteDefect` route lives
 * in 088 alongside the restore companion.
 */
export function useSoftDeleteDefectMutation(inspectionId: string) {
  const qc = useQueryClient();
  const api = useApi();
  return useMutation<
    { deleted: true; id: string; previous?: Defect },
    Error & { errorId?: string },
    { defectId: string }
  >({
    mutationFn: async ({ defectId }) => {
      const response = await api.delete<{
        deleted: true;
        id: string;
        previous?: Defect;
      }>(`/defects/${encodeURIComponent(defectId)}`);
      return response.data;
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: [...defectKeys.all, "list", inspectionId],
      });
    },
  });
}

/**
 * Story 064 AC5 — inverse of {@link useSoftDeleteDefectMutation}.
 * Spec: docs/backend-requirements/088-restore-defect.md.
 */
export function useRestoreDefectMutation(inspectionId: string) {
  const qc = useQueryClient();
  const api = useApi();
  return useMutation<
    { defect: Defect },
    Error & { errorId?: string },
    { defectId: string; previousStatus: DefectStatus }
  >({
    mutationFn: async ({ defectId, previousStatus }) => {
      const response = await api.post<{ defect: Defect }>(
        `/defects/${encodeURIComponent(defectId)}/restore`,
        { previousStatus },
      );
      return response.data;
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: [...defectKeys.all, "list", inspectionId],
      });
    },
  });
}

/**
 * Story 064 — UNDOABLE wrapper around {@link useUpdateDefectMutation}.
 *
 * Same shape as the base mutation; in addition:
 *   1. Captures the optimistic-update closure's `before` snapshot.
 *   2. On settle (200), appends an `update` row to the audit log.
 *   3. On audit-log success, pushes an `updateDefect` entry onto the
 *      undo stack keyed by the **caller-supplied** `imageId` (the
 *      active image at mutate time — not the defect's `imageId`, which
 *      is the same value in normal use but stays decoupled so the
 *      caller controls when the entry lands).
 *
 * The wrapper deliberately fires audit-log + stack push from `onSuccess`
 * — if the PATCH 500s we don't want a phantom stack entry the user
 * could undo. The base mutation's rollback path still runs.
 *
 * Callers pass the **current `Defect`** snapshot so we can compute the
 * `before` shape without re-reading the cache. `catalogueLabel` is
 * filled in (best-effort) by the caller from the catalogue entries
 * lookup; the wrapper does not look it up itself to stay decoupled
 * from the catalogue store.
 */
export interface UndoableUpdateVars {
  defectId: string;
  body: UpdateDefectBody;
  /** Pre-mutation snapshot — captured by the caller. */
  before: UpdateDefectBefore;
  /** Post-mutation snapshot — captured by the caller. */
  after: UpdateDefectBefore;
  /** Owning data-acquisition id for the audit row. */
  dataAcquisitionId: string;
  /** Active image id at mutate time; drives the per-image undo stack. */
  imageId: string;
}

export function useUndoableUpdateDefectMutation(inspectionId: string) {
  const base = useUpdateDefectMutation(inspectionId);
  const audit = useAuditLogAppendMutation();

  return useMutation<
    UpdateDefectResponse,
    Error & { errorId?: string },
    UndoableUpdateVars
  >({
    mutationFn: async (vars) => {
      const data = await base.mutateAsync({
        defectId: vars.defectId,
        body: vars.body,
      });
      // Sequence the audit-log append after the PATCH settles. If the
      // append itself 500s we LOG to console — the FE undo stack still
      // accepts the entry (with a placeholder auditLogId) so the
      // analyst's local Ctrl+Z keeps working. PR-D's audit-log writer
      // will make this load-bearing.
      let auditResult: AuditLogAppendResponse | null = null;
      try {
        auditResult = await audit.mutateAsync({
          entityType: "defect",
          entityId: vars.defectId,
          dataAcquisitionId: vars.dataAcquisitionId,
          action: "update",
          before: vars.before as Record<string, unknown>,
          after: vars.after as Record<string, unknown>,
        });
      } catch (e: unknown) {
        console.warn("[064] audit-log append failed (degraded)", e);
      }
      const auditLogId =
        auditResult?.auditLogId ??
        `local-${Math.random().toString(36).slice(2)}`;
      const entry = {
        kind: "updateDefect" as const,
        imageId: vars.imageId,
        defectId: vars.defectId,
        before: vars.before,
        after: vars.after,
        auditLogId,
        description: "",
      };
      const description = describeUndoEntry({
        ...entry,
        pushedAtMs: Date.now(),
      });
      useUndoStackStore.getState().push({ ...entry, description });
      return data;
    },
  });
}

/**
 * Story 064 — UNDOABLE wrapper around {@link useSoftDeleteDefectMutation}.
 * Pushes a `softDeleteDefect` entry onto the per-image undo stack and
 * appends an audit-log row.
 */
export interface UndoableSoftDeleteVars {
  defectId: string;
  defectSnapshot: Defect;
  dataAcquisitionId: string;
  imageId: string;
}

export function useUndoableSoftDeleteDefectMutation(inspectionId: string) {
  const base = useSoftDeleteDefectMutation(inspectionId);
  const audit = useAuditLogAppendMutation();

  return useMutation<
    { deleted: true; id: string },
    Error & { errorId?: string },
    UndoableSoftDeleteVars
  >({
    mutationFn: async (vars) => {
      const data = await base.mutateAsync({ defectId: vars.defectId });
      let auditResult: AuditLogAppendResponse | null = null;
      try {
        auditResult = await audit.mutateAsync({
          entityType: "defect",
          entityId: vars.defectId,
          dataAcquisitionId: vars.dataAcquisitionId,
          action: "softDelete",
          before: { ...vars.defectSnapshot },
          after: null,
        });
      } catch (e: unknown) {
        console.warn("[064] audit-log append failed (degraded)", e);
      }
      const auditLogId =
        auditResult?.auditLogId ??
        `local-${Math.random().toString(36).slice(2)}`;
      useUndoStackStore.getState().push({
        kind: "softDeleteDefect",
        imageId: vars.imageId,
        defect: vars.defectSnapshot,
        auditLogId,
        description: `delete defect ${vars.defectSnapshot.catalogueLabel}`,
      });
      return data;
    },
  });
}

/**
 * Story 024 — focus-equals-review status transition.
 *
 * Spec: docs/backend-requirements/090-defect-status-transition.md.
 *
 * Sends `PATCH /defects/:id/status` with `{ targetStatus, reviewPhase }`.
 * Optimistically flips the defect in cache; rolls back on error.
 * Audit-log row + undo-stack entry are appended on success.
 */
export interface TransitionDefectStatusVars {
  defectId: string;
  /** Current wire-status BEFORE the optimistic flip — used for rollback + undo. */
  fromStatus: DefectStatus;
  targetStatus: "reviewed" | "confirmed";
  reviewPhase: "PRE_SCREENING" | "EXPERT_ANALYSIS";
  /** Owning data acquisition — needed for the audit row. */
  dataAcquisitionId: string;
  /** Active image id at mutate time; drives the per-image undo stack. */
  imageId: string;
}

export interface TransitionDefectStatusResponse {
  defect: Defect;
  reviewPhase: "PRE_SCREENING" | "EXPERT_ANALYSIS";
  defectReviewStateId: string;
}

interface TransitionContext {
  previousLists: { key: readonly unknown[]; data: GetDefectsResponse }[];
}

export function useTransitionDefectStatusMutation(inspectionId: string) {
  const qc = useQueryClient();
  const api = useApi();
  const audit = useAuditLogAppendMutation();

  return useMutation<
    TransitionDefectStatusResponse,
    Error & { errorId?: string },
    TransitionDefectStatusVars,
    TransitionContext
  >({
    mutationFn: async (vars) => {
      const response = await api.patch<TransitionDefectStatusResponse>(
        `/defects/${encodeURIComponent(vars.defectId)}/status`,
        { targetStatus: vars.targetStatus, reviewPhase: vars.reviewPhase },
      );
      return response.data;
    },
    onMutate: async (vars) => {
      const queryFamily = [...defectKeys.all, "list", inspectionId] as const;
      await qc.cancelQueries({ queryKey: queryFamily });
      const cached = qc.getQueriesData<GetDefectsResponse>({
        queryKey: queryFamily,
      });
      const ctx: TransitionContext = {
        previousLists: cached.map(([key, data]) => ({
          key,
          data: data ?? { items: [], total: 0, page: 1, pageSize: 50 },
        })),
      };
      for (const [key, data] of cached) {
        if (!data) continue;
        qc.setQueryData<GetDefectsResponse>(key, {
          ...data,
          items: data.items.map(
            (d): Defect =>
              d.id === vars.defectId
                ? {
                    ...d,
                    status: vars.targetStatus,
                    // Best-effort denormalised audit timestamp for the
                    // optimistic path. The server response will replace it.
                    updatedAt: new Date().toISOString(),
                  }
                : d,
          ),
        });
      }
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      for (const { key, data } of ctx.previousLists) {
        qc.setQueryData(key, data);
      }
    },
    onSuccess: async (_data, vars) => {
      // Audit-log row (best-effort). Failure is logged, not surfaced.
      let auditResult: AuditLogAppendResponse | null = null;
      try {
        auditResult = await audit.mutateAsync({
          entityType: "defect",
          entityId: vars.defectId,
          dataAcquisitionId: vars.dataAcquisitionId,
          action: "update",
          before: { status: vars.fromStatus },
          after: { status: vars.targetStatus },
        });
      } catch (e: unknown) {
        console.warn("[024] audit-log append failed (degraded)", e);
      }
      const auditLogId =
        auditResult?.auditLogId ??
        `local-${Math.random().toString(36).slice(2)}`;
      // Push to the per-image undo stack. Story 024 transitions ARE
      // undoable per the brief — Ctrl+Z reverses the auto-advance.
      // The undo store's existing `updateDefect` kind serialises the
      // before/after, and the existing inverse-PATCH handler hits the
      // same `/defects/:id` editing surface to roll back. The local
      // `reviewed` value is widened via `as` since the undo store
      // shape still types `status` as the canonical contract enum.
      useUndoStackStore.getState().push({
        kind: "updateDefect",
        imageId: vars.imageId,
        defectId: vars.defectId,
        before: { status: vars.fromStatus },
        after: { status: vars.targetStatus },
        auditLogId,
        description: `status ${vars.fromStatus} → ${vars.targetStatus}`,
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: [...defectKeys.all, "list", inspectionId],
      });
    },
  });
}

/**
 * Story 025 — bulk-verify (verify all filter-visible defects).
 *
 * Spec: docs/backend-requirements/091-bulk-verify-defects.md.
 *
 * Sends `POST /data-acquisitions/:id/defects/bulk-verify` and
 * optimistically advances every defect in `defectIds` in the cache.
 * On success pushes a single compound `bulkVerify` undo entry; on
 * error rolls back the optimistic flip.
 */
export interface BulkVerifyVars {
  dataAcquisitionId: string;
  fromPhase: "PRE_SCREENING" | "EXPERT_ANALYSIS";
  defectIds: string[];
  /** Active image id at mutate time; drives the per-image undo stack
   *  bucket the compound entry lives in. */
  imageId: string;
}

export interface BulkVerifyResponse {
  fromPhase: "PRE_SCREENING" | "EXPERT_ANALYSIS";
  toPhase: "reviewed" | "confirmed";
  verifiedCount: number;
  skippedCount: number;
  defects: Defect[];
}

interface BulkVerifyContext {
  previousLists: { key: readonly unknown[]; data: GetDefectsResponse }[];
}

/**
 * Story 029 — Defect linking. Mutations against
 * `POST /defects/:masterId/links` + `DELETE /defect-links/:linkId`.
 * Spec: docs/backend-requirements/094-defect-links.md.
 *
 * Both push to the per-image undo stack (kinds `linkDefects` /
 * `unlinkDefects`) and append a forward-only audit-log row. On success
 * the defects list AND the per-defect `/links` query family invalidate
 * so the link icon + popover update in lock-step.
 */
export interface DefectLink {
  linkId: string;
  masterDefectId: string;
  linkedDefectId: string;
  createdAt: string;
  createdBy: string;
  reason?: string | null;
}

export interface ListDefectLinksItem {
  linkId: string;
  siblingDefectId: string;
  isMaster: boolean;
  siblingImageId: string;
  siblingCatalogueLabel: string;
  /** 8.0.0 (severity story) — nullable; null = the sibling is not graded. */
  siblingSeverity: "info" | "low" | "medium" | "high" | "critical" | null;
  siblingThumb120: string;
  createdAt: string;
}

export interface ListDefectLinksResponse {
  items: ListDefectLinksItem[];
}

export const defectLinkKeys = {
  all: ["defect-links"] as const,
  forDefect: (defectId: string) =>
    [...defectLinkKeys.all, "list", defectId] as const,
};

export function useDefectLinksQuery(defectId: string) {
  const api = useApi();
  return useQuery<ListDefectLinksResponse>({
    queryKey: defectLinkKeys.forDefect(defectId),
    enabled: defectId.length > 0,
    queryFn: async () => {
      const response = await api.get<ListDefectLinksResponse>(
        `/defects/${encodeURIComponent(defectId)}/links`,
      );
      return response.data;
    },
    staleTime: 30_000,
  });
}

export interface LinkDefectsVars {
  masterId: string;
  linkedDefectId: string;
  reason?: string;
  dataAcquisitionId: string;
  /** Active image id at mutate time — drives the per-image undo stack. */
  imageId: string;
}

export function useLinkDefectsMutation(inspectionId: string) {
  const qc = useQueryClient();
  const api = useApi();
  const audit = useAuditLogAppendMutation();
  return useMutation<DefectLink, Error & { errorId?: string }, LinkDefectsVars>(
    {
      mutationFn: async (vars) => {
        const response = await api.post<DefectLink>(
          `/defects/${encodeURIComponent(vars.masterId)}/links`,
          {
            linkedDefectId: vars.linkedDefectId,
            ...(vars.reason ? { reason: vars.reason } : {}),
          },
        );
        // Best-effort audit-log + undo-stack push. Failures degrade
        // gracefully — the link itself is committed.
        let auditResult: AuditLogAppendResponse | null = null;
        try {
          auditResult = await audit.mutateAsync({
            entityType: "defectLink",
            entityId: response.data.linkId,
            dataAcquisitionId: vars.dataAcquisitionId,
            action: "linkDefects",
            before: null,
            after: {
              masterDefectId: vars.masterId,
              linkedDefectId: vars.linkedDefectId,
            },
          });
        } catch (e: unknown) {
          console.warn("[029] audit-log append failed (degraded)", e);
        }
        const auditLogId =
          auditResult?.auditLogId ??
          `local-${Math.random().toString(36).slice(2)}`;
        useUndoStackStore.getState().push({
          kind: "linkDefects",
          imageId: vars.imageId,
          linkId: response.data.linkId,
          auditLogId,
          description: `link defect ${vars.linkedDefectId} → ${vars.masterId}`,
        });
        return response.data;
      },
      onSuccess: (link) => {
        // Invalidate the defect list AND the link list for both sides
        // of the new relationship so badges + popovers refresh.
        void qc.invalidateQueries({
          queryKey: [...defectKeys.all, "list", inspectionId],
        });
        void qc.invalidateQueries({
          queryKey: defectLinkKeys.forDefect(link.masterDefectId),
        });
        void qc.invalidateQueries({
          queryKey: defectLinkKeys.forDefect(link.linkedDefectId),
        });
      },
    },
  );
}

export interface UnlinkDefectsVars {
  linkId: string;
  /** The two sides — used to invalidate both link queries on success. */
  masterDefectId: string;
  linkedDefectId: string;
  dataAcquisitionId: string;
  imageId: string;
}

export function useUnlinkDefectsMutation(inspectionId: string) {
  const qc = useQueryClient();
  const api = useApi();
  const audit = useAuditLogAppendMutation();
  return useMutation<
    { deleted: true; linkId: string },
    Error & { errorId?: string },
    UnlinkDefectsVars
  >({
    mutationFn: async (vars) => {
      const response = await api.delete<{ deleted: true; linkId: string }>(
        `/defect-links/${encodeURIComponent(vars.linkId)}`,
      );
      let auditResult: AuditLogAppendResponse | null = null;
      try {
        auditResult = await audit.mutateAsync({
          entityType: "defectLink",
          entityId: vars.linkId,
          dataAcquisitionId: vars.dataAcquisitionId,
          action: "unlinkDefects",
          before: {
            masterDefectId: vars.masterDefectId,
            linkedDefectId: vars.linkedDefectId,
          },
          after: null,
        });
      } catch (e: unknown) {
        console.warn("[029] audit-log append failed (degraded)", e);
      }
      const auditLogId =
        auditResult?.auditLogId ??
        `local-${Math.random().toString(36).slice(2)}`;
      useUndoStackStore.getState().push({
        kind: "unlinkDefects",
        imageId: vars.imageId,
        linkId: vars.linkId,
        auditLogId,
        description: `unlink defect ${vars.linkedDefectId} from ${vars.masterDefectId}`,
      });
      return response.data;
    },
    onSuccess: (_resp, vars) => {
      void qc.invalidateQueries({
        queryKey: [...defectKeys.all, "list", inspectionId],
      });
      void qc.invalidateQueries({
        queryKey: defectLinkKeys.forDefect(vars.masterDefectId),
      });
      void qc.invalidateQueries({
        queryKey: defectLinkKeys.forDefect(vars.linkedDefectId),
      });
    },
  });
}

export function useBulkVerifyDefectsMutation(inspectionId: string) {
  const qc = useQueryClient();
  const api = useApi();

  return useMutation<
    BulkVerifyResponse,
    Error & { errorId?: string },
    BulkVerifyVars,
    BulkVerifyContext
  >({
    mutationFn: async (vars) => {
      const response = await api.post<BulkVerifyResponse>(
        `/data-acquisitions/${encodeURIComponent(vars.dataAcquisitionId)}/defects/bulk-verify`,
        { fromPhase: vars.fromPhase, defectIds: vars.defectIds },
      );
      return response.data;
    },
    onMutate: async (vars) => {
      const queryFamily = [...defectKeys.all, "list", inspectionId] as const;
      await qc.cancelQueries({ queryKey: queryFamily });
      const cached = qc.getQueriesData<GetDefectsResponse>({
        queryKey: queryFamily,
      });
      const ctx: BulkVerifyContext = {
        previousLists: cached.map(([key, data]) => ({
          key,
          data: data ?? { items: [], total: 0, page: 1, pageSize: 50 },
        })),
      };
      const targetStatus: DefectStatus =
        vars.fromPhase === "EXPERT_ANALYSIS" ? "confirmed" : "reviewed";
      const targetIds = new Set(vars.defectIds);
      for (const [key, data] of cached) {
        if (!data) continue;
        qc.setQueryData<GetDefectsResponse>(key, {
          ...data,
          items: data.items.map(
            (d): Defect =>
              targetIds.has(d.id) ? { ...d, status: targetStatus } : d,
          ),
        });
      }
      return ctx;
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx) return;
      for (const { key, data } of ctx.previousLists) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: [...defectKeys.all, "list", inspectionId],
      });
    },
  });
}
